import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';

const InstitutionalPaymentsSubscriptionsPage = () => {
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  const billingData = useMemo(
    () => ({
      plan: {
        name: 'Professional',
        cadence: 'Annual',
        price: '$48,600/year',
        status: 'Active',
        renewalDate: 'May 15, 2026',
        seatsIncluded: 300,
      },
      usage: {
        activeUsers: 242,
        userSeatLimit: 300,
        evaluationsThisMonth: 1384,
        includedEvaluationsPerMonth: 1500,
        storageUsedGb: 68,
        storageLimitGb: 100,
      },
      paymentMethod: {
        brand: 'Visa',
        last4: '4242',
        expMonth: '11',
        expYear: '2028',
        billingEmail: 'billing@institution.org',
      },
      billingContact: {
        institutionName: 'Institution Name',
        contactName: 'Alicia Morgan',
        email: 'alicia.morgan@institution.org',
        phone: '(555) 217-4932',
        address: '245 Medical Center Drive, Suite 300, Chicago, IL 60611',
      },
      invoices: [
        {
          id: 'INV-2026-004',
          date: 'Apr 01, 2026',
          amount: '$48,600.00',
          status: 'Paid',
          issueDate: 'Apr 01, 2026',
          dueDate: 'Apr 15, 2026',
          invoiceFor: 'Institution Plan - Professional',
          issuedBy: 'Medhive Resident Management Platform LLC',
          paymentTerm: '14 days',
          project: 'Institutional Admin Platform',
          items: [
            { description: 'Base platform subscription', quantity: 1, rate: '$300.00', amount: '$300.00' },
            { description: 'Additional active users', quantity: 20, rate: '$5.00', amount: '$100.00' },
          ],
        },
        {
          id: 'INV-2026-003',
          date: 'Mar 01, 2026',
          amount: '$48,600.00',
          status: 'Paid',
          issueDate: 'Mar 01, 2026',
          dueDate: 'Mar 15, 2026',
          invoiceFor: 'Institution Plan - Professional',
          issuedBy: 'Medhive Resident Management Platform LLC',
          paymentTerm: '14 days',
          project: 'Institutional Admin Platform',
          items: [
            { description: 'Base platform subscription', quantity: 1, rate: '$300.00', amount: '$300.00' },
            { description: 'Additional active users', quantity: 20, rate: '$5.00', amount: '$100.00' },
          ],
        },
        {
          id: 'INV-2026-002',
          date: 'Feb 01, 2026',
          amount: '$48,600.00',
          status: 'Open',
          issueDate: 'Feb 01, 2026',
          dueDate: 'Feb 15, 2026',
          invoiceFor: 'Institution Plan - Professional',
          issuedBy: 'Medhive Resident Management Platform LLC',
          paymentTerm: '14 days',
          project: 'Institutional Admin Platform',
          items: [
            { description: 'Base platform subscription', quantity: 1, rate: '$300.00', amount: '$300.00' },
            { description: 'Additional active users', quantity: 20, rate: '$5.00', amount: '$100.00' },
          ],
        },
        {
          id: 'INV-2026-001',
          date: 'Jan 01, 2026',
          amount: '$48,600.00',
          status: 'Paid',
          issueDate: 'Jan 01, 2026',
          dueDate: 'Jan 15, 2026',
          invoiceFor: 'Institution Plan - Professional',
          issuedBy: 'Medhive Resident Management Platform LLC',
          paymentTerm: '14 days',
          project: 'Institutional Admin Platform',
          items: [
            { description: 'Base platform subscription', quantity: 1, rate: '$300.00', amount: '$300.00' },
            { description: 'Additional active users', quantity: 20, rate: '$5.00', amount: '$100.00' },
          ],
        },
      ],
      upcomingPayment: {
        amount: '$48,600.00',
        chargeDate: 'May 01, 2026',
        method: 'Visa ending in 4242',
        invoiceId: 'INV-2026-005',
      },
      usageTrend: [
        { month: 'Nov', evaluations: 980, activeUsers: 204 },
        { month: 'Dec', evaluations: 1044, activeUsers: 210 },
        { month: 'Jan', evaluations: 1120, activeUsers: 218 },
        { month: 'Feb', evaluations: 1215, activeUsers: 226 },
        { month: 'Mar', evaluations: 1328, activeUsers: 236 },
        { month: 'Apr', evaluations: 1384, activeUsers: 242 },
      ],
    }),
    []
  );

  const breadcrumbItems = useMemo(
    () => [
      { href: '/institutional-admin/dashboard', label: 'Inst. Admin' },
      { label: 'Payments & Subscriptions' },
    ],
    []
  );

  const storagePercent = Math.min(
    Math.round((billingData.usage.storageUsedGb / billingData.usage.storageLimitGb) * 100),
    100
  );
  const seatsPercent = Math.min(
    Math.round((billingData.usage.activeUsers / billingData.usage.userSeatLimit) * 100),
    100
  );
  const evaluationsPercent = Math.min(
    Math.round((billingData.usage.evaluationsThisMonth / billingData.usage.includedEvaluationsPerMonth) * 100),
    100
  );
  const maxTrendEvaluations = Math.max(...billingData.usageTrend.map((item) => item.evaluations), 1);

  const filteredInvoices = useMemo(() => {
    const search = invoiceSearch.trim().toLowerCase();
    return billingData.invoices.filter((invoice) => {
      const statusMatches = invoiceStatusFilter === 'all' || invoice.status.toLowerCase() === invoiceStatusFilter;
      const searchMatches =
        !search ||
        invoice.id.toLowerCase().includes(search) ||
        invoice.date.toLowerCase().includes(search) ||
        invoice.amount.toLowerCase().includes(search);
      return statusMatches && searchMatches;
    });
  }, [billingData.invoices, invoiceSearch, invoiceStatusFilter]);

  const statusVariant = {
    paid: 'success',
    open: 'warning',
    failed: 'danger',
  };
  const openInvoiceModal = (invoice) => setSelectedInvoice(invoice);
  const closeInvoiceModal = () => setSelectedInvoice(null);

  return (
    <div className="d-flex vh-100 overflow-hidden bg-light">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <div className="flex-grow-1 p-4">
          <Container fluid>
            {(storagePercent >= 85 || evaluationsPercent >= 85 || seatsPercent >= 85) && (
              <Alert variant="warning" className="mb-3">
                <div className="fw-semibold mb-1">Usage is approaching plan limits</div>
                <div className="small">
                  Seats: {seatsPercent}% | Evaluations: {evaluationsPercent}% | Storage: {storagePercent}%
                </div>
              </Alert>
            )}
            {billingData.invoices.some((invoice) => invoice.status.toLowerCase() === 'open') && (
              <Alert variant="info" className="mb-3">
                One or more invoices are currently open. Review billing history to avoid service interruptions.
              </Alert>
            )}

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
              <div>
                <h4 className="mb-1">Payments & Subscriptions</h4>
                <div className="text-muted">Manage your institution billing profile and usage.</div>
              </div>
              <Button variant="outline-primary" disabled>
                Manage subscription
              </Button>
            </div>

            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="text-muted small mb-1">Upcoming Payment</div>
                  <h6 className="mb-1">{billingData.upcomingPayment.amount} due on {billingData.upcomingPayment.chargeDate}</h6>
                  <div className="small text-muted">
                    Charged to {billingData.upcomingPayment.method} • Invoice {billingData.upcomingPayment.invoiceId}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openInvoiceModal({
                      ...billingData.invoices[0],
                      id: billingData.upcomingPayment.invoiceId,
                      status: 'Pending Payment',
                      date: billingData.upcomingPayment.chargeDate,
                      issueDate: billingData.upcomingPayment.chargeDate,
                      dueDate: billingData.upcomingPayment.chargeDate,
                    })}
                  >
                    View upcoming invoice
                  </Button>
                  <Button size="sm" variant="success">
                    Pay now
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Row className="g-3 mb-4">
              <Col lg={4}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted small mb-1">Current Plan</div>
                    <h5 className="mb-1">{billingData.plan.name}</h5>
                    <div className="mb-2">{billingData.plan.price}</div>
                    <Badge bg="success">{billingData.plan.status}</Badge>
                    <hr />
                    <div className="small text-muted">Billing cadence</div>
                    <div className="mb-2">{billingData.plan.cadence}</div>
                    <div className="small text-muted">Renewal date</div>
                    <div className="mb-2">{billingData.plan.renewalDate}</div>
                    <div className="small text-muted">Included seats</div>
                    <div>{billingData.plan.seatsIncluded}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted small mb-1">Usage Data</div>
                    <h5 className="mb-3">Current period</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Active users</span>
                      <strong>{billingData.usage.activeUsers}</strong>
                    </div>
                    <ProgressBar className="mb-3" now={seatsPercent} variant={seatsPercent >= 90 ? 'danger-solid' : 'success-solid'} />
                    <div className="d-flex justify-content-between mb-2">
                      <span>Evaluations this month</span>
                      <strong>{billingData.usage.evaluationsThisMonth}</strong>
                    </div>
                    <ProgressBar className="mb-3" now={evaluationsPercent} variant={evaluationsPercent >= 90 ? 'danger-solid' : 'primary-solid'} />
                    <div className="d-flex justify-content-between mb-2">
                      <span>Storage used</span>
                      <strong>
                        {billingData.usage.storageUsedGb} GB / {billingData.usage.storageLimitGb} GB
                      </strong>
                    </div>
                    <ProgressBar
                      now={storagePercent}
                      label={`${storagePercent}%`}
                      variant={storagePercent >= 90 ? 'danger-solid' : 'orange-solid'}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="text-muted small mb-1">Payment Method</div>
                    <h5 className="mb-2">
                      {billingData.paymentMethod.brand} ending in {billingData.paymentMethod.last4}
                    </h5>
                    <div className="mb-3">Expires {billingData.paymentMethod.expMonth}/{billingData.paymentMethod.expYear}</div>
                    <div className="small text-muted">Billing email</div>
                    <div className="mb-3">{billingData.paymentMethod.billingEmail}</div>
                    <Button size="sm" variant="outline-secondary" disabled>
                      Update payment method
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col xl={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Usage Trend (Last 6 Months)</h6>
                    <small className="text-muted">Mock data preview</small>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      {billingData.usageTrend.map((item) => {
                        const percentage = Math.round((item.evaluations / maxTrendEvaluations) * 100);
                        return (
                          <Col key={item.month} md={2} sm={4} xs={6}>
                            <div className="text-muted small">{item.month}</div>
                            <ProgressBar now={percentage} style={{ height: 12 }} />
                            <div className="small mt-1">{item.evaluations} evaluations</div>
                            <div className="small text-muted">{item.activeUsers} active users</div>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3">
              <Col xl={5}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white">
                    <h6 className="mb-0">Billing Details</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <div className="text-muted small">Institution</div>
                      <div>{billingData.billingContact.institutionName}</div>
                    </div>
                    <div className="mb-2">
                      <div className="text-muted small">Billing contact</div>
                      <div>{billingData.billingContact.contactName}</div>
                    </div>
                    <div className="mb-2">
                      <div className="text-muted small">Email</div>
                      <div>{billingData.billingContact.email}</div>
                    </div>
                    <div className="mb-2">
                      <div className="text-muted small">Phone</div>
                      <div>{billingData.billingContact.phone}</div>
                    </div>
                    <div>
                      <div className="text-muted small">Address</div>
                      <div>{billingData.billingContact.address}</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={7}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Billing History</h6>
                    <Button variant="outline-secondary" size="sm">
                      Export
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2 mb-3">
                      <Col md={5}>
                        <Form.Control
                          size="sm"
                          placeholder="Search invoice ID, date, amount"
                          value={invoiceSearch}
                          onChange={(event) => setInvoiceSearch(event.target.value)}
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Select
                          size="sm"
                          value={invoiceStatusFilter}
                          onChange={(event) => setInvoiceStatusFilter(event.target.value)}
                        >
                          <option value="all">All statuses</option>
                          <option value="paid">Paid</option>
                          <option value="open">Open</option>
                          <option value="failed">Failed</option>
                        </Form.Select>
                      </Col>
                    </Row>
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th className="ps-3">Invoice</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th className="pe-3 text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.length > 0 ? (
                          filteredInvoices.map((invoice) => {
                            const normalizedStatus = invoice.status.toLowerCase();
                            return (
                              <tr key={invoice.id}>
                                <td className="ps-3">
                                  <Button
                                    variant="link"
                                    className="p-0 text-decoration-none"
                                    onClick={() => openInvoiceModal(invoice)}
                                  >
                                    {invoice.id}
                                  </Button>
                                </td>
                                <td>{invoice.date}</td>
                                <td>{invoice.amount}</td>
                                <td>
                                  <Badge bg={statusVariant[normalizedStatus] || 'secondary'}>{invoice.status}</Badge>
                                </td>
                                <td className="pe-3 text-end">
                                  <div className="d-inline-flex gap-2">
                                    <Button size="sm" variant="outline-primary" onClick={() => openInvoiceModal(invoice)}>
                                      View
                                    </Button>
                                    <Button size="sm" variant="outline-secondary">
                                      Download
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center py-4 text-muted">
                              No invoices found for the current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <Modal show={!!selectedInvoice} onHide={closeInvoiceModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Invoice Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <div className="border rounded p-3 p-md-4 bg-white">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <img src="/images/asset11.svg" alt="App logo" style={{ height: 36 }} />
                    <img src="/images/asset5.svg" alt="App logo" style={{ height: 18 }} />
                  </div>
                  <h5 className="mt-3 mb-1">Invoice {selectedInvoice.id}</h5>
                  <div className="small text-muted">
                    Issue Date: {selectedInvoice.issueDate} • Due Date: {selectedInvoice.dueDate}
                  </div>
                </div>
                <div className="text-end">
                  <Badge bg={statusVariant[selectedInvoice.status.toLowerCase()] || 'secondary'} className="mb-2">
                    {selectedInvoice.status}
                  </Badge>
                  <div className="fw-semibold fs-5">{selectedInvoice.amount}</div>
                </div>
              </div>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div className="text-muted small">Issue For</div>
                  <div className="fw-semibold">{selectedInvoice.invoiceFor}</div>
                  <div className="text-muted small mt-3">Issued By</div>
                  <div className="fw-semibold">{selectedInvoice.issuedBy}</div>
                </Col>
                <Col md={6}>
                  <div className="text-muted small">Payment Details</div>
                  <div className="fw-semibold">{billingData.paymentMethod.brand} ending in {billingData.paymentMethod.last4}</div>
                  <div>{billingData.paymentMethod.billingEmail}</div>
                  <div className="mt-3 text-muted small">Payment Term</div>
                  <div className="fw-semibold">{selectedInvoice.paymentTerm}</div>
                </Col>
              </Row>

              <Table responsive className="mb-3 custom-modern-responsive">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items.map((item) => (
                    <tr key={item.description}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{item.rate}</td>
                      <td className="text-end">{item.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-end me-2 mt-4">
                <div style={{ maxWidth: 320, width: '100%', flexShrink: 1 }}>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Subtotal</span>
                    <span>{selectedInvoice.amount}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Tax</span>
                    <span>$0.00</span>
                  </div>
                  <div className="d-flex justify-content-between fw-semibold border-top pt-2 mt-2">
                    <span>Total</span>
                    <span>{selectedInvoice.amount}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InstitutionalPaymentsSubscriptionsPage;
